import { type TypedDocumentNode, gql } from '@apollo/client';
import { useDrag } from '@use-gesture/react';
import classNames from 'classnames';
import { CSSProperties, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { animated, easings, useSpring, useSprings } from 'react-spring';
import styled from 'styled-components';

import { DropCardInput } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { AddCards } from '@sorare/core/src/atoms/icons/AddCards';
import { DropCards } from '@sorare/core/src/atoms/icons/DropCards';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { ValidWidths } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { CardImg } from '@sorare/core/src/components/card/CardImg';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';
import { useSetAutocleanTimeout } from '@sorare/core/src/hooks/useSetAutocleanTimeout';
import { glossary } from '@sorare/core/src/lib/glossary';

import { BaseLayout, CardsArea } from '../RevealPackCards/ui';
import type {
  TinderCardSwipe_anyCardInterface,
  dropPackCardsMutation,
  dropPackCardsMutationVariables,
} from './__generated__/index.graphql';

const CardsWrapper = styled(CardsArea)`
  position: relative;
  aspect-ratio: var(--pack-aspect-ratio);
`;
const TranslateWrapper = styled(animated.div)`
  width: 100%;
  aspect-ratio: var(--pack-aspect-ratio);
  transform: translateY(calc(var(--translate-factor) * var(--stripe-height)));
`;
const CardImgWrapper = styled(animated.div)`
  position: absolute;
  user-select: none;
  touch-action: none;
  padding: 0 var(--double-unit);
  & > img {
    user-select: none;
    touch-action: none;
  }
`;
const BrightEffectWrapper = styled.div`
  padding: 0 var(--double-unit);
  opacity: 1;

  &.opened {
    opacity: 0;
    transition: opacity linear 0.5s 0.45s;
  }
`;
const BrightEffect = styled.div`
  filter: brightness(15) blur(2px) drop-shadow(0 0 30px #ffffff);
  opacity: calc((var(--progress) - 30) / 100);

  &.opened {
    opacity: 1;
    transition: opacity linear 0.4s;
  }
`;
const ClipPath = styled.div`
  clip-path: rect(0 100% calc(100% - var(--stripe-height)) 0);
`;
const CardsOpacityWrapper = styled.div`
  opacity: 0;

  &.opened {
    opacity: 1;
    transition: opacity linear 0.1s 0.2s;
  }
`;
const CTAsWrapper = styled(Vertical)`
  padding-top: var(--quadruple-unit);
`;

const DROP_PACK_CARDS_MUTATION = gql`
  mutation dropPackCardsMutation($input: dropAndAddCardsInput!) {
    dropAndAddCards(input: $input) {
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<dropPackCardsMutation, dropPackCardsMutationVariables>;

type Props = {
  cards: TinderCardSwipe_anyCardInterface[];
  cardWidth?: ValidWidths;
  packOpened: boolean;
  onContinue: () => void;
};
export const TinderCardSwipe = ({
  cards,
  cardWidth = 320,
  packOpened,
  onContinue,
}: Props) => {
  const [verticalMovementStarted, setVerticalMovementStarted] = useState(false);
  const [brightEffectDone, setBrightEffectDone] = useState(false);
  const [springStyles, api] = useSprings(cards.length, springIndex => ({
    x: 0,
    zIndex: -1 * springIndex,
  }));
  const [verticalMovementStyle, verticalMovementApi] = useSpring(() => ({
    '--translate-factor': 1,
  }));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [droppedCards, setDroppedCards] = useState<DropCardInput[]>([]);
  const setAutocleanTimeout = useSetAutocleanTimeout();
  const isLaptopAndAbove = useIsLaptop();

  const containerWidth = isLaptopAndAbove ? 440 : window.innerWidth;
  const swipedCardFinalOffset = containerWidth + 200;

  const [dropCards, { loading }] = useMutation(DROP_PACK_CARDS_MUTATION, {
    showErrorsWithSnackNotification: true,
  });

  const moveToNextIndex = () => {
    if (currentIndex < cards.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };
  const dropCurrentCard = () => {
    const currentCard = cards[currentIndex];

    if (currentCard) {
      const { slug, signedDropCardPrice } = currentCard;

      if (signedDropCardPrice) {
        setDroppedCards(prev => [
          ...prev,
          { cardSlug: slug, signedPrice: signedDropCardPrice.signedPrice },
        ]);
      }
    }
  };

  if (packOpened && !verticalMovementStarted) {
    verticalMovementApi.start({
      '--translate-factor': 0,
      config: { duration: 400, easing: easings.easeInOutQuad },
      onRest: () => {
        verticalMovementApi.start({
          '--translate-factor': 1,
          config: { duration: 500, easing: easings.easeInOutQuad },
          delay: 50,
        });
      },
    });
    setVerticalMovementStarted(true);
    setAutocleanTimeout(() => {
      setBrightEffectDone(true);
    }, 900);
  }

  const moveCardWithoutDrag = ({ isDrop }: { isDrop: boolean }) => {
    if (isDrop) {
      dropCurrentCard();
    }
    moveToNextIndex();

    const direction = isDrop ? -1 : 1;
    api.start(i => {
      if (currentIndex !== i) return {};

      return {
        x: direction * swipedCardFinalOffset,
        config: {
          tension: 100,
        },
      };
    });
  };

  const dropCardsAndContinue = async () => {
    await dropCards({ variables: { input: { droppedCards } } });
    onContinue();
  };

  const bind = useDrag(
    ({ args: [{ index }], movement: [mx], cancel, last }) => {
      if (Math.abs(mx) > cardWidth / 1.2) {
        cancel();
      }
      const isGone = Math.abs(mx) > cardWidth / 2;
      const direction = mx > 0 ? 1 : -1;

      api.start(i => {
        if (index !== i) return {};
        if (last && !isGone) {
          return {
            x: 0,
            config: {
              tension: 150,
            },
          };
        }
        const x = isGone ? swipedCardFinalOffset * direction : mx;

        if (isGone && currentIndex === i) {
          if (direction < 0) {
            dropCurrentCard();
          }
          moveToNextIndex();
        }

        return {
          x,
          config: {
            tension: isGone ? 150 : 800,
            friction: 50,
          },
        };
      });
    }
  );

  return (
    <BaseLayout>
      <CardsWrapper>
        <TranslateWrapper style={verticalMovementStyle as CSSProperties}>
          <CardsOpacityWrapper className={classNames({ opened: packOpened })}>
            {cards.map((card, index) => {
              return (
                <CardImgWrapper
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  {...bind({ index })}
                  style={springStyles[index]}
                >
                  <CardImg
                    width={cardWidth}
                    src={card.pictureUrl}
                    draggable={false}
                    alt={card.name}
                  />
                </CardImgWrapper>
              );
            })}
          </CardsOpacityWrapper>
          {!brightEffectDone && (
            <BrightEffectWrapper className={classNames({ opened: packOpened })}>
              <BrightEffect className={classNames({ opened: packOpened })}>
                <ClipPath>
                  <CardImg
                    width={cardWidth}
                    src={cards[0]?.pictureUrl}
                    draggable={false}
                  />
                </ClipPath>
              </BrightEffect>
            </BrightEffectWrapper>
          )}
        </TranslateWrapper>
      </CardsWrapper>
      {brightEffectDone && (
        <CTAsWrapper>
          <SBHorizontal>
            <IconButton
              color="red"
              onClick={() => {
                moveCardWithoutDrag({ isDrop: true });
              }}
            >
              <DropCards />
            </IconButton>
            <IconButton
              color="secondary"
              onClick={() => {
                moveCardWithoutDrag({ isDrop: false });
              }}
            >
              <AddCards />
            </IconButton>
          </SBHorizontal>
          {currentIndex === cards.length - 1 && (
            <LoadingButton
              color="secondary"
              onClick={dropCardsAndContinue}
              size="medium"
              loading={loading}
            >
              <FormattedMessage {...glossary.continue} />
            </LoadingButton>
          )}
        </CTAsWrapper>
      )}
    </BaseLayout>
  );
};

TinderCardSwipe.fragments = {
  anyCardInterface: gql`
    fragment TinderCardSwipe_anyCardInterface on AnyCardInterface {
      slug
      pictureUrl
      name
      signedDropCardPrice {
        signedPrice
      }
    }
  ` as TypedDocumentNode<TinderCardSwipe_anyCardInterface>,
};
